import { SwapParams } from '~/components/forms/swap/SwapFormSection'
import { SwapQuoteError } from '~/components/forms/swap/hooks/swapQuoteError'
import { REFETCH_SWAP_QUOTE } from '~/constants/refetch-intervals'

// React Query uses 3 for queries. The retry predicate replaces that default.
const QUOTE_RETRY_COUNT = 3

// Six doublings of 10 seconds end at 320 seconds, which keeps one open form under the server 4xx alert warning of 25 requests per hour.
const MAX_BACKOFF_STEPS = 6

// A failure of these types usually repeats until the user changes the pair.
const ERROR_TYPES_LIKELY_TO_KEEP_FAILING = ['NoRoute', 'UnsupportedToken']

// Holds the last pair that failed and how many times in a row. A quote or a page reload clears it.
let lastFailure: { pair: string; failures: number } | undefined

const getPair = ({ sourceToken, destinationToken }: SwapParams) =>
  [
    sourceToken.chainId,
    sourceToken.token,
    destinationToken.chainId,
    destinationToken.token,
  ].join(':')

const getBackoffInterval = (failures: number) =>
  REFETCH_SWAP_QUOTE * 2 ** (Math.min(failures, MAX_BACKOFF_STEPS) - 1)

const isLikelyToKeepFailing = (error: unknown) =>
  error instanceof SwapQuoteError &&
  error.errorType !== undefined &&
  ERROR_TYPES_LIKELY_TO_KEEP_FAILING.includes(error.errorType)

export const shouldRetryQuote = (failureCount: number, error: unknown) =>
  !isLikelyToKeepFailing(error) && failureCount < QUOTE_RETRY_COUNT

export const recordQuoteError = ({
  params,
  error,
}: {
  params: SwapParams
  error: unknown
}) => {
  if (!isLikelyToKeepFailing(error)) return

  const pair = getPair(params)
  const failures = lastFailure?.pair === pair ? lastFailure.failures + 1 : 1
  lastFailure = { pair, failures }
}

export const clearQuoteError = (params: SwapParams) => {
  if (lastFailure?.pair === getPair(params)) lastFailure = undefined
}

export const getQuoteRefetchInterval = ({
  params,
  error,
}: {
  params: SwapParams | undefined
  error: unknown
}) => {
  const isSamePair = !!params && lastFailure?.pair === getPair(params)
  if (!isSamePair || !lastFailure || !isLikelyToKeepFailing(error))
    return REFETCH_SWAP_QUOTE

  return getBackoffInterval(lastFailure.failures)
}

export const __testing__ = {
  forgetLastFailure: () => {
    lastFailure = undefined
  },
}
